import { motion } from "framer-motion";
import { ArrowRight, Home, Layers, Sparkles } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

export default function Pricing({ onRequest }) {
    const { t } = useT();
    const tiers = [
        {
            icon: Home,
            label: t("pricing.single_label"),
            price: t("pricing.single_price"),
            sub: t("pricing.single_sub"),
            badge: "01",
        },
        {
            icon: Layers,
            label: t("pricing.combo_label"),
            price: t("pricing.combo_price"),
            sub: t("pricing.combo_sub"),
            badge: "02",
            featured: true,
        },
    ];

    return (
        <section
            id="pricing"
            className="relative py-24 md:py-32 bg-charcoal"
            data-testid="pricing-section"
        >
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-10">
                    <div>
                        <div className="flex items-center gap-3 mb-5">
                            <span className="inline-block w-8 h-[1px] bg-gold" />
                            <span className="text-[11px] tracking-[0.4em] uppercase text-gold font-semibold">
                                {t("pricing.overline")}
                            </span>
                        </div>
                        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-semibold max-w-2xl leading-[1.05]">
                            {t("pricing.heading")}
                        </h2>
                    </div>
                    <p className="text-white/60 max-w-md text-sm md:text-base leading-relaxed">
                        {t("pricing.subhead")}
                    </p>
                </div>

                {/* Airbnb partnership strip */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="mb-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-10 p-6 md:p-8 border border-white/10 bg-charcoal"
                    data-testid="airbnb-partnership-strip"
                >
                    <div className="flex items-center gap-4 shrink-0">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-gold font-semibold">
                            {t("pricing.partner_label")}
                        </div>
                        <img
                            src="https://customer-assets.emergentagent.com/job_wynwood-short-term/artifacts/pypfj7v6_Imagen%2021-4-26%20a%20las%2021.25.jpeg"
                            alt="Airbnb"
                            className="h-8 md:h-10 w-auto object-contain"
                            style={{
                                filter: "invert(1) brightness(1.15) contrast(1.1)",
                                mixBlendMode: "screen",
                            }}
                            data-testid="airbnb-logo"
                        />
                    </div>
                    <div className="h-px md:h-10 md:w-px bg-white/10" />
                    <p className="text-sm md:text-base text-white/70 leading-relaxed">
                        {t("pricing.partner_copy")}
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {tiers.map((tier, i) => {
                        const Icon = tier.icon;
                        return (
                            <motion.div
                                key={tier.label}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.7, delay: i * 0.1 }}
                                className={`relative p-8 md:p-10 border transition-colors ${
                                    tier.featured
                                        ? "border-gold bg-gold/5"
                                        : "border-white/10 bg-charcoal-surface hover:border-gold/40"
                                }`}
                                data-testid={`pricing-tier-${i}`}
                            >
                                <div className="flex items-start justify-between gap-4 mb-8">
                                    <div
                                        className={`w-12 h-12 flex items-center justify-center ${
                                            tier.featured
                                                ? "bg-gold text-charcoal"
                                                : "bg-charcoal border border-white/10 text-gold"
                                        }`}
                                    >
                                        <Icon size={20} strokeWidth={1.5} />
                                    </div>
                                    <span className="font-display text-xs text-white/40">
                                        {tier.badge}
                                    </span>
                                </div>
                                <div className="text-[11px] tracking-[0.3em] uppercase text-white/60 mb-3">
                                    {tier.label}
                                </div>
                                <div
                                    className={`font-display text-4xl md:text-5xl font-bold tracking-tight ${
                                        tier.featured ? "text-gold" : "text-white"
                                    }`}
                                >
                                    {tier.price}
                                </div>
                                <div className="mt-4 inline-flex items-center gap-1.5 border border-gold/40 text-gold px-2.5 py-1 text-[10px] tracking-[0.25em] uppercase font-semibold">
                                    <Sparkles size={10} strokeWidth={2.5} />
                                    {t("pricing.furnished_chip")}
                                </div>
                                <p className="mt-5 text-sm md:text-base text-white/60 leading-relaxed">
                                    {tier.sub}
                                </p>
                            </motion.div>
                        );
                    })}
                </div>

                <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                    <p className="text-xs text-white/40 italic max-w-2xl">
                        {t("pricing.disclaimer")}
                    </p>
                    <button
                        onClick={onRequest}
                        className="inline-flex items-center gap-3 border border-gold text-gold px-6 py-3 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-gold hover:text-charcoal transition-colors"
                        data-testid="pricing-cta-btn"
                    >
                        {t("pricing.cta")}
                        <ArrowRight size={14} />
                    </button>
                </div>
            </div>
        </section>
    );
}
